import {RdpConnection} from './rdp-connection';
import {ConnectionEvent} from './enum/connection-event';
import {ConnectionState} from './enum/connection-state';
import {ConnectionFactoryType} from './enum/connection-factory-type';
import {PlatformInfo} from './platform-info';
import {PrintingService} from '../printing.service';
import {Logger} from './logger';
import {TelemetryService} from '../telemetry.service';
import {ConnectionProperties} from './connection-properties';
import {ConnectionWrapper} from './ConnectionWrapper';

declare const Module: any;

export class ConnectionFactory {
    private readonly connections: ConnectionWrapper[] = [];

    constructor(
        private readonly factoryType: ConnectionFactoryType,
        private readonly logger: Logger,
        private readonly audioContext: AudioContext,
        private readonly telemetry: TelemetryService,
        private readonly printing: PrintingService,
        private readonly platformInfo: PlatformInfo,
        private readonly depG: any,
        private readonly adfsTokenProvider: { getADFSToken: (props: ConnectionProperties) => Promise<string> } | any
    ) {}

    private readonly onConnectionConnectFailed = (connection: RdpConnection): void => {
        this.logger.error?.('[ConnectionFactory] ConnectionConnectFailed', {
            id: connection?.id,
            state: connection?.getState?.(),
            remoteAppId: (connection as any)?.remoteAppId
        });
        this.removeConnection(connection);
    };

    private readonly onConnectionDestroyed = (connection: RdpConnection): void => {
        const userInitiated =
            !!connection?.disconnectReason && connection.disconnectReason.code === Module.DisconnectCode.UserInitiated;

        if (!userInitiated) {
            this.removeConnection(connection);
        }
    };

    private readonly onWindowsLoaded = (connectionId: string): void => {
        for (let i = 0; i < this.connections.length; i++) {
            if (this.connections[i].connection.id === connectionId) {
                this.connections[i].windowsLoaded = true;
                break;
            }
        }
    };

    private findConnectionInternal(connectionProperties: ConnectionProperties): RdpConnection | null {
        for (let i = 0; i < this.connections.length; i++) {
            if (this.connections[i].properties.isEqual(connectionProperties)) {
                return this.connections[i].connection;
            }
        }
        return null;
    }

    findConnection(connectionProperties: ConnectionProperties): RdpConnection | null {
        return this.findConnectionInternal(connectionProperties);
    }

    findConnectionById(id: string): RdpConnection | null {
        for (let i = 0; i < this.connections.length; i++) {
            if (this.connections[i].connection.id === id) {
                return this.connections[i].connection;
            }
        }
        return null;
    }

    removeConnection(connection: RdpConnection): void {
        for (let i = 0; i < this.connections.length; i++) {
            if (this.connections[i].connection === connection) {
                this.connections.splice(i, 1);
                break;
            }
        }
        connection?.dismiss?.();
    }

    createConnection(
        connectionProperties: ConnectionProperties,
        onReady: (rdpConnection: RdpConnection) => void,
        remoteAppId: number
    ): void {
        let existing = this.findConnectionInternal(connectionProperties);

        if (existing && existing.getState() === ConnectionState.Disconnected) {
            this.removeConnection(existing);
            existing = null;
        }

        if (existing) {
            existing.isExistingConnection = true;
            onReady(existing);
            return;
        }

        const createWithToken = (adfsToken: string): void => {
            try {
                connectionProperties.connectionSettings.ADFSToken = adfsToken;

                const correlationId = Module.CorrelationID.CreateRandomCorrelationID();
                const nativeConnection = Module.IConnection.Create(connectionProperties.connectionSettings, correlationId);

                const rdpConnection = new RdpConnection(
                    nativeConnection,
                    this.logger,
                    this.audioContext,
                    connectionProperties.remoteApplicationMode,
                    this.telemetry,
                    this.printing,
                    this.platformInfo,
                    this.depG,
                    remoteAppId
                );

                rdpConnection.id = correlationId.ToString().slice(1, -1);
                rdpConnection.correlationId = correlationId;
                rdpConnection.tenantId = connectionProperties.tenantId;

                if (connectionProperties.connectionSettings?.EventLogUploadAddress) {
                    rdpConnection.eventLogUploadAddress = connectionProperties.connectionSettings.EventLogUploadAddress;
                }

                this.connections.push({
                    connection: rdpConnection,
                    properties: connectionProperties,
                    windowsLoaded: false
                });

                rdpConnection.events.subscribe(ConnectionEvent.ConnectionDestroyed, this.onConnectionDestroyed);
                rdpConnection.events.subscribe(ConnectionEvent.ConnectionConnectFailed, this.onConnectionConnectFailed);
                rdpConnection.events.subscribe(ConnectionEvent.WindowsLoaded, this.onWindowsLoaded);

                rdpConnection.isExistingConnection = false;
                onReady(rdpConnection);
            } catch (err) {
                this.logger.error?.('[ConnectionFactory] Failed to create connection', err);
                throw err;
            }
        };

        if (this.factoryType !== ConnectionFactoryType.RdpConnection) {
            this.logger.error?.('[ConnectionFactory] Unsupported factory type', this.factoryType);
            throw new Error('Unsupported ConnectionFactoryType');
        }

        const needsAdfsToken = !!connectionProperties.connectionSettings?.GatewayCertLogonAuthority && !!this.adfsTokenProvider;

        if (!needsAdfsToken) {
            createWithToken('');
            return;
        }

        try {
            const tokenPromise = this.adfsTokenProvider.getADFSToken(connectionProperties);

            tokenPromise.then(
                (token: string) => createWithToken(token || ''),
                (err: any) => {
                    this.logger.error?.('[ConnectionFactory] Failed to get ADFS token; continuing without token', err);
                    createWithToken('');
                }
            );
        } catch (err) {
            this.logger.error?.('[ConnectionFactory] Error while requesting ADFS token; continuing without token', err);
            createWithToken('');
        }
    }

    disconnectAllConnections(): void {
        let wrapper = this.connections.shift();
        while (wrapper && wrapper.connection) {
            wrapper.connection.disconnect(true);
            wrapper = this.connections.shift();
        }
    }

    invalidateCanvasForActiveConnections(): void {
        for (let i = 0; i < this.connections.length; i++) {
            this.connections[i].connection.invalidateCanvas();
        }
    }

    hasActiveConnections(): boolean {
        return this.connections.length !== 0;
    }

    listActiveConnections(): RdpConnection[] {
        return this.connections.map(w => w.connection);
    }

    windowsLoaded(): boolean {
        for (let i = 0; i < this.connections.length; i++) {
            if (this.connections[i].windowsLoaded !== true) {
                return false;
            }
        }
        return true;
    }
}
